import { Component, OnInit, Input } from '@angular/core';
import { FileUploadService } from '@shared/api/file-upload/file-upload.service';
import { SessionStorageService } from '@shared/services/session-storage/session-storage.service';
import { HttpEventType } from '@angular/common/http';
import { Subscriber } from 'rxjs';
import { DragAndDropService } from '@shared/services/drag-and-drop/drag-and-drop.service';
import { CurrentUserService } from '@shared/services/current-user/current-user.service';

@Component({
  selector: 'drag-and-drop',
  templateUrl: './drag-and-drop.component.html',
  styleUrls: ['./drag-and-drop.component.scss']
})
export class DragAndDropComponent implements OnInit {
  files;
  placeHolder;
  toUploadFiles;
  identity;
  allValid;
  userName;

  constructor(
    private _fileUploadService: FileUploadService,
    private _sessionStorageService: SessionStorageService,
    private _dragAndDropService: DragAndDropService,
    private _currentUserService: CurrentUserService
  ) {}

  @Input()
  set setPlaceHolder(text) {
    this.placeHolder = text;
  }

  @Input()
  set setIdentity(name) {
    this.identity = name;
  }

  ngOnInit() {
    this.resetDragAndDrop();
    this._dragAndDropService.uploadingFiles.subscribe(() => {
      this.onSubmit();
    });
    this._dragAndDropService.abortingAllUploads.subscribe(() => {
      this.abortAllUploads();
    });
    this._dragAndDropService.resettingDragAndDrop.subscribe(() => {
      this.resetDragAndDrop();
    });
  }

  resetDragAndDrop() {
    this.allValid = true;
    this.files = [];
    this.toUploadFiles = [];
    this.userName = this._currentUserService.getUserInfo().userName;
  }

  onFileChanged(event) {
    for (const file of event.target.files) {
      this.files.push(file);
      this.toUploadFiles.push({
        id: this.generateUniqueId(),
        name: file.name,
        size: this.sizeIndicator(file.size),
        byteSize: file.size,
        uploading: false,
        validFileName: this.isASCIICharacters(file.name),
        validFileType: this.isValidFileType(file.name),
        progress: `0%`,
        sizeProgress: `0KB`,
        progressColor: this.updateProgressColor('success'),
        isGreaterThan10MB: this.fileSizeChecker(file.size),
        uploadSuccess: false,
        observable: Subscriber
      });
    }
    this._dragAndDropService.setTotalBytes(
      this.identity,
      this.extractTotalBytes(this.toUploadFiles)
    );
    this._dragAndDropService.setExpectedFileIds(
      this.identity,
      this.extractFileIds(this.toUploadFiles)
    );

    this._dragAndDropService.setUploadingStatus(
      this.identity,
      this.extractUploadingStatus(this.toUploadFiles)
    );
    this.updateFlags();
  }

  extractTotalBytes(files) {
    const temp = files.filter(
      file => !this._dragAndDropService.isFileAlreadyUploaded(file.id)
    );
    return temp.reduce((acc, cur) => acc + parseInt(cur.byteSize, 0), 0);
  }

  extractFileIds(files) {
    return files.map(file => file.id);
  }

  extractUploadingStatus(files) {
    return files.map(file => {
      return { id: file.id, uploading: file.uploading };
    });
  }

  generateUniqueId() {
    return parseInt(
      new Date()
        .valueOf()
        .toString()
        .concat(this.files.length),
      10
    );
  }
  uploadFiles(file, userName, index) {
    const observable = this._fileUploadService
      .uploadFiles(file, userName)
      .subscribe(
        evt => {
          if (evt.type === HttpEventType.UploadProgress) {
            this.toUploadFiles[index].progressColor = this.updateProgressColor(
              'success'
            );
            const percentage = Math.round(
              (evt.loaded / this.toUploadFiles[index].byteSize) * 100
            );
            this.toUploadFiles[index].progress =
              percentage < 100 ? `${percentage}%` : `100%`;
            this.toUploadFiles[index].sizeProgress =
              evt.loaded < this.toUploadFiles[index].byteSize
                ? this.sizeIndicator(evt.loaded)
                : this.sizeIndicator(this.toUploadFiles[index].byteSize);

            this._dragAndDropService.updateUploadPercentage(
              evt.loaded,
              this.toUploadFiles[index].id
            );

            if (this.toUploadFiles[index].progress === `100%`) {
              this._dragAndDropService.updateSuccessfulFileIds(
                this.toUploadFiles[index].id,
                'ADD'
              );
            }
          }
          if (evt.type === HttpEventType.Response) {
            this.toUploadFiles[index].uploading = false;
            this.toUploadFiles[index].uploadSuccess = true;
            this._dragAndDropService.updateUploadStatus(
              this.toUploadFiles[index].id,
              this.toUploadFiles[index].uploading
            );
            this._dragAndDropService.updatingFileIds.emit(
              this._dragAndDropService.isAllFilesSuccessful()
            );
            this._dragAndDropService.updateSubmitText();
          }
        },
        error => {
          this.toUploadFiles[index].progressColor = this.updateProgressColor(
            'danger'
          );
          this.toUploadFiles[index].uploading = false;
          this.toUploadFiles[index].uploadSuccess = false;
          this._dragAndDropService.updateUploadStatus(
            this.toUploadFiles[index].id,
            this.toUploadFiles[index].uploading
          );
          this._dragAndDropService.updateSuccessfulFileIds(
            this.toUploadFiles[index].id,
            'REMOVE'
          );
          this._dragAndDropService.updateNumberOfFailedFiles(1);
          this._dragAndDropService.setInterruptedVerificationStatus(true);
        }
      );
    this.toUploadFiles[index].observable = observable;
    this.toUploadFiles[index].uploading = true;
    this._dragAndDropService.updateUploadStatus(
      this.toUploadFiles[index].id,
      this.toUploadFiles[index].uploading
    );
  }

  sizeIndicator(bytes) {
    const oneMB = 125000;
    if (bytes >= oneMB) {
      return `${(bytes * 0.000001).toFixed(2)}MB`;
    } else {
      return `${(bytes * 0.001).toFixed(2)}KB`;
    }
  }

  fileSizeChecker(bytes) {
    const MBsize = bytes * 0.000001;
    if (MBsize > 10) {
      this.allValid = false;
    }
    return MBsize > 10;
  }

  deleteFile(id) {
    this.toUploadFiles = this.toUploadFiles.filter((file, index) => {
      if (file.id === id) {
        this.files.splice(index, 1);
      } else {
        return file;
      }
    });
    this.fileValidityChecker();
    this._dragAndDropService.setTotalBytes(
      this.identity,
      this.extractTotalBytes(this.toUploadFiles)
    );
    this._dragAndDropService.setExpectedFileIds(
      this.identity,
      this.extractFileIds(this.toUploadFiles)
    );
    this._dragAndDropService.setUploadingStatus(
      this.identity,
      this.extractUploadingStatus(this.toUploadFiles)
    );

    if (this._dragAndDropService.isReSubmitted()) {
      this._dragAndDropService.setSubmitButtonStatus(true);
      if (this.identity === 2 || this.identity === 3) {
        this._dragAndDropService.updateNetWorthExpectedFileIds();
        this._dragAndDropService.updateNetWorthUploadingStatus();
      }
      this._dragAndDropService.updateNumberOfFailedFiles(-1);
    }

    this.updateFlags();
  }

  cancelFileUpload(file) {
    file.observable.unsubscribe();
    file.uploading = false;
    if (this.identity === 2 || this.identity === 3) {
      this._dragAndDropService.updateNetWorthTotalBytes();
    }
    file.progressColor = this.updateProgressColor('warning');
  }

  onSubmit() {
    this.allValid = true;
    this.files.forEach((file, index) => {
      if (
        this._dragAndDropService.isFileAlreadyUploaded(
          this.toUploadFiles[index].id
        )
      ) {
        this._dragAndDropService.updateSuccessfulFileIds(file.id, 'REMOVE');
      } else {
        this.toUploadFiles[index].progress = '0%';
        this.toUploadFiles[index].sizeProgress = `0KB`;
        this.uploadFiles(file, this.userName, index);
      }
    });
  }

  updateFlags() {
    const size = this.files.length;
    if (this.identity === 2) {
      this._dragAndDropService.setFlagNetWorthFilesI(this.allValid && !!size);
    } else if (this.identity === 3) {
      this._dragAndDropService.setFlagNetWorthFilesII(this.allValid && !!size);
    } else {
      this._dragAndDropService.setFlagForVerificationFiles(
        this.allValid && !!size
      );
    }
  }

  resetFilesSelected(ref) {
    ref.files = [];
    ref.toUploadFiles = [];
    this.allValid = true;
  }

  isASCIICharacters(name) {
    const isASCII = /^[\x00-\x7F]*$/.test(name);
    if (!isASCII) {
      this.allValid = false;
    }
    return isASCII;
  }

  updateProgressColor(classColor) {
    return classColor;
  }

  fileValidityChecker() {
    const errorFiles = this.toUploadFiles.filter(file => {
      if (
        file.progressColor === 'danger' ||
        file.isGreaterThan10MB ||
        !file.validFileName
      ) {
        return file;
      }
    });

    if (errorFiles.length === 0) {
      this.allValid = true;
      this.updateFlags();
    }
  }

  abortAllUploads() {
    this.files.forEach((file, index) => {
      if (
        !this._dragAndDropService.isFileAlreadyUploaded(
          this.toUploadFiles[index].id
        )
      ) {
        if (this.toUploadFiles[index].uploading) {
          this.cancelFileUpload(this.toUploadFiles[index]);
        }
      }
    });
  }

  isValidFileType(fileName) {
    const valid = [
      '.jpg',
      '.jpeg',
      '.txt',
      '.doc',
      '.docx',
      '.pdf',
      '.png'
    ].includes(
      fileName.slice(fileName.lastIndexOf('.'), fileName.length).toLowerCase()
    );
    if (!valid) {
      this.allValid = false;
    }
    return valid;
  }
}
